import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Party } from '../../app/feathers/parties/Party';

interface CreatePartyModalProps {
  onCreate: (party: Party) => void;
  buttonComponent?: React.ComponentType<{ onClick: () => void }>;
}

export const CreatePartyModal: React.FC<CreatePartyModalProps> = ({
  onCreate,
  buttonComponent: ButtonComponent,
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [modalOpen, setModalOpen] = useState(false);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = () => {
    setModalOpen(true);
    setKey('');
    setName('');
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    setModalOpen(false);
    setKey('');
    setName('');
    setError(null);
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    const trimmedKey = key?.trim();
    const trimmedName = name?.trim();
    if (!trimmedKey || !trimmedName) {
      setError('Key and name are required.');
      return;
    }
    if (!feathers) {
      setError('Unable to create party: not connected.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const party = await feathers.getService('parties').create({
        key: trimmedKey,
        name: trimmedName,
      });
      onCreate(party);
      handleClose();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to create party.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {ButtonComponent && (
        <ButtonComponent onClick={handleOpen} />
      )}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Add Party</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g. D, R, L"
              size="small"
              fullWidth
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Democrat, Republican"
              size="small"
              fullWidth
            />
            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting || !key?.trim() || !name?.trim()}
          >
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
